import React, { useMemo, useState } from 'react';
import { DatePicker, Select, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useManpowerDay } from '../../hooks/useStudioOps';
import BayHistogram, { ConsistencyDial } from '../../components/LiveShoots/BayHistogram';
import ExportExcel from '../../commonComponents/Export-Excel';
import { CREW_SHIFTS, crewShift, crewShiftText } from './crewShift';
import styles from './index.module.css';

const CET_ZONE = 'Europe/Berlin';

function cetShiftDate(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: CET_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(now);
    const pick = (type) => parts.find((part) => part.type === type).value;
    const hour = Number(pick('hour'));
    const minute = Number(pick('minute'));
    const calendar = `${pick('year')}-${pick('month')}-${pick('day')}`;
    if (hour > 6 || (hour === 6 && minute >= 30)) return calendar;
    const previous = new Date(`${calendar}T12:00:00Z`);
    previous.setUTCDate(previous.getUTCDate() - 1);
    const month = String(previous.getUTCMonth() + 1).padStart(2, '0');
    const day = String(previous.getUTCDate()).padStart(2, '0');
    return `${previous.getUTCFullYear()}-${month}-${day}`;
}

function formatCetTime(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
        timeZone: CET_ZONE,
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
    });
}

function shiftDurationMs(row) {
    return typeof row?.shiftMs === 'number' ? row.shiftMs : null;
}

function formatShift(ms) {
    if (ms == null) return '—';
    if (ms === 0) return '0m';
    return formatDurationMs(ms);
}

function formatDurationMs(ms) {
    if (typeof ms !== 'number' || ms <= 0) return '—';
    const minutes = Math.round(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours && mins) return `${hours}h ${mins}m`;
    if (hours) return `${hours}h`;
    return `${mins}m`;
}

function operatorName(row) {
    return row.operator?.name || row.operator?.email || 'Unknown';
}

function operatorType(row) {
    if (row.operator?.affiliation === 'agency') return 'Agency';
    if (row.operator?.affiliation === 'fx') return 'FX team';
    return '—';
}

function operatorContact(row) {
    if (row.operator?.affiliation === 'agency') return row.operator?.agency || '—';
    return row.operator?.email || '—';
}

const WORK_COLORS = {
    pii_scan: '#2e90fa',
    pii_done: '#175cd3',
    minibatch_scan: '#7a5af8',
    pack_scan: '#12b76a',
    audit_scan: '#f79009',
    outbound_scan: '#f04438',
    photographer_scan: '#ee46bc',
    stylist_scan: '#15b79e',
    shoot_complete: '#667085',
    article_search: '#444ce7',
    repii_resolve: '#6941c6',
    shoot_photographer: '#667085',
    shoot_stylist: '#0e9384',
};

function workKinds(types) {
    return (types || []).map((type, index) => ({
        key: type.key,
        label: type.label,
        color: WORK_COLORS[type.key] || ['#b54708', '#3538cd', '#98a2b3'][index % 3],
    }));
}

function shiftRank(row) {
    const id = crewShift(row.firstScanAt)?.id;
    return id ? Number(id) : 99;
}

function rowCompare(a, b, key) {
    if (key === 'operator') return operatorName(a).localeCompare(operatorName(b));
    if (key === 'shift') return shiftRank(a) - shiftRank(b);
    if (key === 'total') return (a.total || 0) - (b.total || 0);
    if (key === 'breakCount') return (a.breakCount || 0) - (b.breakCount || 0);
    if (key === 'breakMs') return (a.breakMs || 0) - (b.breakMs || 0);
    if (key === 'shiftMs') return (shiftDurationMs(a) || 0) - (shiftDurationMs(b) || 0);
    return (a.byType?.[key] || 0) - (b.byType?.[key] || 0);
}

function workLines(row, types) {
    return (types || [])
        .filter((type) => row.byType?.[type.key])
        .map((type) => type.label);
}

function rowMatchesWork(row, selected) {
    if (!selected.length) return true;
    return selected.some((key) => row.byType?.[key]);
}

function rowMatchesShift(row, shiftId) {
    if (!shiftId) return true;
    return crewShift(row.firstScanAt)?.id === shiftId;
}

function sumType(rows, key) {
    return (rows || []).reduce((total, row) => total + (row.byType?.[key] || 0), 0);
}

function manpowerSheet(rows, types) {
    const data = (rows || []).map((row, index) => {
        const sheet = {
            '#': index + 1,
            Operator: operatorName(row),
            Type: operatorType(row),
            Shift: crewShiftText(row.firstScanAt),
            'Agency / Email': operatorContact(row),
            Work: workLines(row, types).join(', '),
        };
        for (const type of types || []) sheet[type.label] = row.byType?.[type.key] || 0;
        sheet.Total = row.total || 0;
        sheet.Consistency = row.consistency == null ? '' : row.consistency;
        sheet.Breaks = row.breakCount || 0;
        sheet['Break time'] = formatDurationMs(row.breakMs);
        sheet['First (CET)'] = formatCetTime(row.firstScanAt);
        sheet['Last (CET)'] = formatCetTime(row.lastScanAt);
        sheet.Hours = formatShift(shiftDurationMs(row));
        return sheet;
    });
    const total = { '#': '', Operator: 'Total', Type: '', Shift: '', 'Agency / Email': '', Work: '' };
    for (const type of types || []) total[type.label] = sumType(rows, type.key);
    total.Total = (rows || []).reduce((sum, row) => sum + (row.total || 0), 0);
    total.Consistency = '';
    total.Breaks = (rows || []).reduce((sum, row) => sum + (row.breakCount || 0), 0);
    total['Break time'] = formatDurationMs((rows || []).reduce((sum, row) => sum + (row.breakMs || 0), 0));
    total['First (CET)'] = '';
    total['Last (CET)'] = '';
    total.Hours = '';
    data.push(total);
    return data;
}

export default function Manpower() {
    const [date, setDate] = useState(cetShiftDate);
    const [works, setWorks] = useState([]);
    const [shiftId, setShiftId] = useState(null);
    const [sort, setSort] = useState({ key: 'total', order: 'descend' });
    const { data, isFetching } = useManpowerDay(date);
    const payload = data?.data || {};
    const rows = payload.data || [];
    const types = payload.types || [];
    const range = payload.range;
    const visibleRows = useMemo(
        () => rows.filter((row) => rowMatchesWork(row, works) && rowMatchesShift(row, shiftId)),
        [rows, works, shiftId]
    );
    const narrowed = works.length > 0 || !!shiftId;
    const sortedRows = useMemo(() => {
        if (!sort.order) return visibleRows;
        const next = [...visibleRows].sort((a, b) => rowCompare(a, b, sort.key));
        return sort.order === 'descend' ? next.reverse() : next;
    }, [visibleRows, sort]);
    const sortOrder = (key) => (sort.key === key ? sort.order : null);

    const columns = [
        {
            title: '#',
            width: 48,
            fixed: 'left',
            align: 'right',
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Operator',
            key: 'operator',
            fixed: 'left',
            width: 180,
            sorter: true,
            sortOrder: sortOrder('operator'),
            render: (_, row) => operatorName(row),
        },
        {
            title: 'Shift',
            key: 'shift',
            width: 148,
            sorter: true,
            sortOrder: sortOrder('shift'),
            render: (_, row) => {
                const shift = crewShift(row.firstScanAt);
                if (!shift) return '—';
                return <Tag color={shift.color}>{crewShiftText(row.firstScanAt)}</Tag>;
            },
        },
        {
            title: 'Type',
            width: 110,
            render: (_, row) => operatorType(row),
        },
        {
            title: 'Agency / Email',
            width: 200,
            render: (_, row) => operatorContact(row),
        },
        {
            title: 'Work',
            width: 280,
            render: (_, row) => {
                const lines = workLines(row, types);
                if (!lines.length) return '—';
                return lines.map((line) => <div key={line}>{line}</div>);
            },
        },
        ...types.map((type) => ({
            title: type.label,
            key: type.key,
            align: 'right',
            width: 120,
            sorter: true,
            sortOrder: sortOrder(type.key),
            render: (_, row) => row.byType?.[type.key] || '—',
        })),
        {
            title: 'Total',
            key: 'total',
            align: 'right',
            width: 90,
            sortOrder: sortOrder('total'),
            sorter: true,
            render: (_, row) => row.total || 0,
        },
        {
            title: 'Breaks',
            key: 'breakCount',
            align: 'right',
            width: 80,
            sorter: true,
            sortOrder: sortOrder('breakCount'),
            render: (_, row) => row.breakCount || 0,
        },
        {
            title: 'Break time',
            key: 'breakMs',
            align: 'right',
            width: 110,
            sorter: true,
            sortOrder: sortOrder('breakMs'),
            render: (_, row) => formatDurationMs(row.breakMs),
        },
        {
            title: 'First',
            width: 150,
            render: (_, row) => formatCetTime(row.firstScanAt),
        },
        {
            title: 'Last',
            width: 150,
            render: (_, row) => formatCetTime(row.lastScanAt),
        },
        {
            title: <span title="First scan to last scan. A gap over 3 hours is left out.">Hours</span>,
            key: 'shiftMs',
            align: 'right',
            width: 100,
            sorter: true,
            sortOrder: sortOrder('shiftMs'),
            render: (_, row) => formatShift(shiftDurationMs(row)),
        },
    ];

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <Typography.Title level={4} className={styles.title}>
                        Manpower
                    </Typography.Title>
                    <Typography.Text type="secondary" className={styles.meta}>
                        Every scan event in the day, grouped by operator and work type. A day is 6:30 AM Europe/Berlin until 6:30 AM the next day. Shift is whichever of 6:00 AM, 2:00 PM, or 10:00 PM is closest to the person's first activity.
                    </Typography.Text>
                </div>
            </div>
            <div className={styles.toolbar}>
                <DatePicker
                    allowClear={false}
                    value={dayjs(date)}
                    onChange={(value) => {
                        if (value) setDate(value.format('YYYY-MM-DD'));
                    }}
                />
                <Select
                    className={styles.shiftFilter}
                    value={shiftId ?? 'all'}
                    options={[
                        { value: 'all', label: 'All shifts' },
                        ...CREW_SHIFTS.map((shift) => ({ value: shift.id, label: `${shift.label} · ${shift.startLabel} CET` })),
                    ]}
                    onChange={(value) => setShiftId(value === 'all' ? null : value)}
                />
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="All work"
                    className={styles.workFilter}
                    value={works}
                    options={types.map((type) => ({ value: type.key, label: type.label }))}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                    onChange={setWorks}
                />
                <Typography.Text type="secondary">
                    {range
                        ? `${formatCetTime(range.start)} – ${formatCetTime(range.end)} Europe/Berlin · ${narrowed ? `${sortedRows.length} of ${rows.length}` : rows.length
                        } operators`
                        : '6:30 AM to next 6:30 AM, Europe/Berlin'}
                </Typography.Text>
                <span className={styles.export}>
                    <ExportExcel excelData={manpowerSheet(sortedRows, types)} fileName={`manpower-${date}`} buttonSize="middle" />
                </span>
            </div>
            <Table
                loading={isFetching}
                pagination={false}
                sticky
                size="small"
                rowKey={(row) =>
                    [row.operator?.userId, row.operator?.email, row.operator?.name, row.firstScanAt].filter(Boolean).join('|') || 'unknown'
                }
                dataSource={sortedRows}
                columns={columns}
                onChange={(_, __, sorter) => {
                    const next = Array.isArray(sorter) ? sorter.find((item) => item.order) || sorter[0] : sorter;
                    setSort({ key: next?.columnKey || null, order: next?.order || null });
                }}
                scroll={{ x: 'max-content', y: 'calc(100vh - 240px)' }}
                expandable={{
                    expandedRowRender: (row) => (
                        <div>
                            <div className={styles.shift}>
                                <div>
                                    <div className={styles.shiftLabel}>Consistency</div>
                                    <ConsistencyDial score={row.consistency} />
                                </div>
                                <BayHistogram
                                    histogram={row.histogram}
                                    kinds={workKinds(types)}
                                    timeZone={CET_ZONE}
                                    zoneLabel="CET"
                                    emptyText="No scans in this window"
                                />
                            </div>
                            <div className={styles.breaks}>
                                <div className={styles.shiftLabel}>Breaks · gaps over 20 minutes and under 3 hours</div>
                                {(row.breaks || []).length ? (
                                    row.breaks.map((item) => (
                                        <div key={`${item.start}-${item.end}`}>
                                            {formatCetTime(item.start)} – {formatCetTime(item.end)} · {formatDurationMs(item.ms)}
                                        </div>
                                    ))
                                ) : (
                                    <Typography.Text type="secondary">No breaks</Typography.Text>
                                )}
                            </div>
                        </div>
                    ),
                    rowExpandable: (row) => (row.histogram || []).length > 0,
                }}
            />
        </div>
    );
}
